import {
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  signal,
  type Type,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { CalendarTenantScopeService } from '@modules/calendar-app/services/calendar-tenant-scope.service';
import { CalendarPanelReturnStateService } from '@modules/calendar-app/services/calendar-panel-return-state.service';
import { ChatPanelStore } from '@modules/chat/services/chat-panel.service';
import { StitchyTeaserService } from '@modules/chat/services/stitchy-teaser.service';
import { PAID_PLANS_STATUS } from '@modules/chat/agent.types';
import {
  MAIN_SPACE_CONTEXT,
  SPACE_CONTEXT_KEY,
} from '@modules/common/injection-tokens/space-context-key.injection-token';
import { ActiveSpaceContextService } from '@modules/common/services/active-space-context.service';
import { type AppTenantScopeService } from '@modules/common/services/app-tenant-scope.service';
import { FormChangesModalsService } from '@modules/common/services/form-changes-modals.service';
import { FullWindowService } from '@modules/common/services/full-window.service';
import { HistoryService, StitchFiltersHistoryRecord } from '@modules/common/services/history.service';
import { MailboxTenantScopeService } from '@modules/common/services/mailbox-tenant-scope.service';
import { NavigationHistoryService } from '@modules/common/services/navigation-history.service';
import { SpaceStateService } from '@modules/common/services/space-state.service';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { StitchEventsService } from '@modules/common/services/stitch-events-service';
import {
  type WorkspaceIdentity,
  WorkspaceIdentityService,
} from '@modules/common/services/workspace-identity.service';
import { StitchServiceFactory } from '@modules/common/factories/stitch-service.factory';
import { Application } from '@modules/common/types/application';
import { chatHomeSpaceId } from '@modules/common/utils/chat-home';
import { APPLICATION_SCOPE_TOKEN, ApplicationScope } from '@modules/common/types/ApplicationScope';
import { Stitch } from '@modules/common/types/stitch';
import { ContactsTenantScopeService } from '@modules/contacts/services/contacts-tenant-scope.service';
import { NotificationPanelDraftsService } from '@modules/desktop-notifications/notification-panel-drafts.service';
import { DpKnowledgePanelService } from '@modules/dynamic-panel/services/dp-knowledge-panel.service';
import { DpSearchLandingService } from '@modules/dynamic-panel/services/dp-search-landing.service';
import { type DynamicPanelApp, DynamicPanelService } from '@modules/dynamic-panel/services/dynamic-panel.service';
import { PanelQuickFormDrafts } from '@modules/dynamic-panel/services/panel-quick-form-drafts';
import { KnowledgePanelService } from '@modules/knowledge/services/knowledge-panel.service';
import { FilesTenantScopeService } from '@modules/files/services/files-tenant-scope.service';
import { NotesTenantScopeService } from '@modules/notes/services/notes-tenant-scope.service';
import { AccountService } from '@frontendlib/common/services/account.service';
import { CurrentUserStore } from '@frontendlib/common/services/current-user.store';
import { PermissionsService } from '@frontendlib/common/features/permissions/services/permissions.service';
import { ProductAnalyticsService } from '@frontendlib/common/services/product-analytics/product-analytics.service';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { AccessLimitService } from '@modules/settings/services/access-limit.service';
import { Space } from '@modules/settings/types/space';
import { SplitViewService } from '@modules/split-view/services/split-view.service';
import { TasksTenantScopeService } from '@modules/tasks/services/tasks-tenant-scope.service';
import { type Actions, type Subjects, ViewSubjects } from '@sharedlib/common/permissions/types/Permissions';
import { SignalStitchEventType } from '@sharedlib/common/types/signal-type';
import { SpaceType } from '@sharedlib/common/types/SpaceType';
import { combineLatest, EMPTY, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, filter, first, map, switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-dynamic-panel',
  templateUrl: './dynamic-panel.component.html',
  styleUrls: ['./dynamic-panel.component.less'],
  standalone: false,
  // HistoryService and KnowledgePanelService are provided at the component level to create
  // isolated instances scoped to this DP. Without this, the DP would share these services
  // with the main app panels — causing issues like selecting a knot/tag in the knowledge panel
  // of the DP also affecting the main app's knowledge panel.
  // With component-level providers, each DP instance gets its own state.
  //
  // Data flow: every marker selection made in the 3rd panel — a form's chips or a Contacts-Hub facet
  // alike — is mirrored into the DP-local HistoryService, so it renders in the knowledge panel's
  // Selected row, answers its queries, and a deselection there is mirrored back (SV1-194, SBUX-708,
  // SBUX-1077). While this panel hosts an item of its own (a form, or an app panel's `holdPanelItem`)
  // nothing crosses.
  // The 3rd panel's open ITEM also reaches us, as contextStitchItem — context, never selection.
  // devdoc/specs/knowledge-panel-selection-scopes.md is the contract; read it before changing any
  // of this.
  //
  // FormChangesModalsService is provided here so any form rendered inside the DP registers
  // against this DP-scoped instance. The root service then no longer sees DP forms, so route
  // guards, space switches, and the main app switcher skip them automatically. DP collapse
  // and DP's own app switcher consult this local instance to gate on unsaved changes.
  // KnowledgePanelService is provided as the DP-scoped DpKnowledgePanelService: same behaviour as
  // the base, except closing a DP form does not reset the DP-local history (which mirrors the
  // parent selection). See DpKnowledgePanelService / SV1-194.
  //
  // DpSearchLandingService is provided here, rather than by the panel that needs it, precisely because
  // this component outlives the panel body: it has to still be watching the panel's search while a form
  // is open, which is when the folder menu that acts on it does not exist. See that service.
  providers: [
    HistoryService,
    DpSearchLandingService,
    { provide: KnowledgePanelService, useClass: DpKnowledgePanelService },
    FormChangesModalsService,
    // The quick forms in the panel body, which a pick of the panel's own workspace would remount (pickWorkspace).
    PanelQuickFormDrafts,
    CalendarPanelReturnStateService,
    {
      provide: NavigationHistoryService,
      useExisting: DynamicPanelService,
    },
    {
      provide: APPLICATION_SCOPE_TOKEN,
      useValue: ApplicationScope.DYNAMIC_PANEL,
    },
    // Names the team and the caller's member tenant for the panel's own workspace menu (TW-PANEL-02).
    WorkspaceIdentityService,
  ],
})
export class DynamicPanelComponent implements OnInit, OnDestroy {
  /**
   * The apps whose panel lists carry a My Workspace / team section of their own. Files too (#2068); a compose that opened
   * the panel's Files for its own owner keeps that owner, and the menu is withheld meanwhile (workspaceChoice).
   */
  private static readonly WORKSPACE_APPS: ReadonlyArray<readonly [Application, Type<AppTenantScopeService>]> = [
    [Application.contacts, ContactsTenantScopeService],
    [Application.tasking, TasksTenantScopeService],
    [Application.notes, NotesTenantScopeService],
    [Application.calendar, CalendarTenantScopeService],
    [Application.mail, MailboxTenantScopeService],
    [Application.files, FilesTenantScopeService],
  ];

  @Input() splitViewKey = 'dp';
  /** Full Chat replaces docked content only; native instances and unrelated floating UI remain alive. */
  @Input() public nativePresentationHidden = false;
  @Input() stateKey: 'modal' | 'default' = 'default';

  public minimized: boolean;
  public selectedApp: DynamicPanelApp;
  public appContextMenuOpened = false;
  public spacesContextMenuOpened = false;
  public Application = Application;
  public space: Space;
  public item: Stitch;

  private alive = new Subject<void>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly stitchEventsService = inject(StitchEventsService);
  private readonly advancedSearchService = inject(AdvancedSearchService);
  protected readonly spaceContextStore = inject(SpaceContextStore);
  private readonly activeSpaceContext = inject(ActiveSpaceContextService);
  private readonly currentUserStore = inject(CurrentUserStore);
  // Whether the panel's lists may mount: a panel in its own context (the layout's 'dp') waits until main's first
  // workspace is applied (#1598 reader F1). One mounted without a context key shares its host's store and has
  // no main area beside it to wait for.
  private readonly spaceContextKey = inject(SPACE_CONTEXT_KEY, { optional: true });
  protected readonly mainWorkspaceApplied = signal(
    !this.spaceContextKey || this.spaceContextKey === MAIN_SPACE_CONTEXT,
  );
  private readonly splitViewService = inject(SplitViewService);
  private readonly productAnalytics = inject(ProductAnalyticsService);

  /** Boards heading "Expand" (prototype `Boards.tsx:317`): the panel's width toggles between 320 and 540 px. */
  protected toggleBoardsWidth(): void {
    const wide = (this.splitViewService.size.getSync(this.splitViewKey) ?? 320) > 320;
    this.splitViewService.size.set(wide ? 320 : 540, this.splitViewKey);
    this.splitViewService.emitSizeChanges();
  }

  /** Boards heading "Close" (prototype `NativePanel.tsx` `onClose`): folds the panel as the split-view toggle does. */
  protected closeBoardsPanel(): void {
    this.splitViewService.toggleClicked(this.splitViewKey);
  }
  private readonly changeDetector = inject(ChangeDetectorRef);
  /** True while a full-window editor session keeps this panel folded to its edge tab (the split view's `temporaryCollapsed`). */
  private readonly fullWindow = inject(FullWindowService);
  protected readonly foldedByWindow = this.fullWindow.dpFolded;
  private readonly dpService = inject(DynamicPanelService);
  // Injected only to bring it to life: it watches the panel's search for as long as this component lives,
  // and the folder menu reads what it recorded whenever the panel body is (re)mounted.
  private readonly searchLandingService = inject(DpSearchLandingService);
  private readonly spaceStateService = inject(SpaceStateService);
  private readonly formChangesModalsService = inject(FormChangesModalsService);
  private readonly permissionsService: PermissionsService<Actions, Subjects> = inject(PermissionsService);
  protected readonly chatStore = inject(ChatPanelStore);
  /** A limited account (failed payment) keeps every app readable; the header says AI and creating are paused. */
  protected readonly accessLimited = inject(AccessLimitService).limited;
  protected readonly hasAgentAccess = computed(() =>
    this.permissionsService.checkPermission('read', ViewSubjects.agentView),
  );
  /** A Free Trial without Stitchy gets its teaser in the same places (SBUX-1231). */
  protected readonly teaser = inject(StitchyTeaserService);
  protected readonly stitchyOffered = computed(() => this.hasAgentAccess() || this.teaser.active());
  /** The teaser's launchers say Paid plans, not Stitchy's own state. */
  protected readonly launcherStatus = computed(() =>
    !this.hasAgentAccess() && this.teaser.active() ? PAID_PLANS_STATUS : null,
  );
  private readonly chatTakesOverSource = computed(
    () => this.stitchyOffered() && this.chatStore.isOpen() && this.chatStore.isDocked(),
  );
  protected readonly chatTakesOver = signal(false);

  /** A docked chat is laid out inside this panel, so it is hidden with it: collapsed, folded by a full-window app, or giving way to the companion. */
  protected get dockedChatHidden(): boolean {
    return (this.minimized || this.foldedByWindow() || this.nativePresentationHidden) && this.chatStore.isDocked();
  }
  // Two distinct instances: localHistoryService is the component-scoped one (from providers above),
  // parentHistoryService is the app-level one (resolved via skipSelf, bypassing this component's
  // provider), which the 3rd panel's form chips and a MAIN-scope Contacts Hub both write.
  private readonly localHistoryService = inject(HistoryService);
  private readonly parentHistoryService = inject(HistoryService, { skipSelf: true });
  // Same two-instance pattern for KnowledgePanelService.
  private readonly localKpService = inject(KnowledgePanelService);
  private readonly parentKpService = inject(KnowledgePanelService, { skipSelf: true });

  // The panel's OWN workspace (TW-PANEL-02, the prototype's workspace-panel.js): it starts in main's workspace and
  // section once, then only its own menu changes it. The scopes are the 'dp' subtree's (the layout's
  // *spaceContext="'dp'"), never main's.
  private readonly injector = inject(Injector);
  private readonly workspaceIdentity = inject(WorkspaceIdentityService);
  private readonly quickFormDrafts = inject(PanelQuickFormDrafts);
  private readonly toasterService = inject(ToasterService);
  private readonly accountService = inject(AccountService);
  private readonly stitchServiceFactory = inject(StitchServiceFactory);
  private readonly appScopes = new Map(
    DynamicPanelComponent.WORKSPACE_APPS.map(([app, token]) => [app, inject(token, { optional: true })] as const),
  );
  private readonly panelApp = signal<DynamicPanelApp>(undefined);
  private readonly panelApp$ = toObservable(this.panelApp);
  private readonly identity = signal<WorkspaceIdentity | undefined>(undefined);
  // Ticks whenever the current app's section may have changed: its scope's stream, the start, a pick.
  private readonly selectionChanged = signal(0);
  // A pick re-keys the panel body once, beside the workspace it lists.
  private readonly pickCount = signal(0);
  protected readonly bodyKey = computed(() => {
    const contextId = this.spaceContextStore.contextId();
    return contextId === undefined ? undefined : `${contextId}:${this.pickCount()}`;
  });
  protected readonly workspaceMenuOpen = signal(false);
  protected readonly closeWorkspaceMenu = new Subject<void>();
  /**
   * The panel's own workspace menu, inside a team while the panel shows one of {@link WORKSPACE_APPS}; null keeps
   * the plain label (a private workspace has no switcher, Pavel 2026-09-10). `mine` is read from the current app's
   * own 'dp' scope, the value its requests carry, never from the context alone.
   */
  protected readonly workspaceChoice = computed(() => {
    this.selectionChanged();
    const identity = this.identity();
    const app = this.panelApp();
    const scope = app ? this.appScopes.get(app) : undefined;
    if (this.spaceContextKey !== 'dp' || !scope || !identity || !this.spaceContextStore.insideTeam()) return null;
    // A Files panel a compose opened lists and creates under the compose's owner (DynamicPanelService.openApp).
    if (app === Application.files && this.dpService.filesTenantId() !== undefined) return null;
    if (identity.spaceTenantId !== this.spaceContextStore.contextId()) return null;
    return { identity, mine: scope.effectiveTenantId() === identity.memberTenantId };
  });

  /**
   * The app menu offers Chat only while the panel stands on the team itself (#1447 S27, TW-PANEL-02): inside a team and
   * not on the member's My Workspace section; an app with no section (Chat, the knowledge panel) counts as the team.
   */
  private readonly accountChatAvailable = signal(false);
  private readonly panelOnTeam = computed(() => {
    this.selectionChanged();
    const team = this.spaceContextStore.contextId();
    if (this.spaceContextKey !== 'dp' || !this.spaceContextStore.insideTeam() || !team) return false;
    // A denied native app can resolve to null; its saved section still determines where this panel stands.
    const app = this.panelApp() ?? this.dpService.state.getSync();
    if (!app || !this.appScopes.has(app)) return true;
    const filesTenant = app === Application.files ? this.dpService.filesTenantId() : undefined;
    if (filesTenant !== undefined) return filesTenant === team;
    const scope = this.appScopes.get(app);
    if (!scope) return false;
    const tenant = scope.tenantId();
    return tenant === undefined || tenant === team;
  });
  protected readonly chatOffered = computed(() => this.accountChatAvailable() && this.panelOnTeam());

  constructor() {
    effect(() => this.dpService.allowChat(this.chatOffered()));
    const unregisterNotificationDrafts = inject(NotificationPanelDraftsService).register(this.formChangesModalsService);
    this.destroyRef.onDestroy(unregisterNotificationDrafts);
    this.destroyRef.onDestroy(this.dpService.registerSurfaceGuard(() => {
      const held = this.quickFormDrafts.holding();
      if (held) {
        this.toasterService.show({
          text: held === 'saving'
            ? 'Wait until the form finishes saving.'
            : 'Finish or cancel the form before switching the side panel app.',
        });
      }
      return of(held === null);
    }));
    this.dpService
      .getFormItem()
      .pipe(takeUntil(this.alive))
      .subscribe((item) => {
        this.item = item;
      });

    // Whether this panel hosts an item of its own: a form (`setFormItem`) or an app panel's held item (`holdPanelItem`).
    const hostsOwnItem$ = combineLatest([
      this.dpService.getFormItem().pipe(map((formItem) => !!formItem)),
      this.dpService.onPanelItemHeldChanges(),
    ]).pipe(
      map(([hasForm, panelItemHeld]) => hasForm || panelItemHeld),
      distinctUntilChanged(),
    );

    // An item taking over the panel clears the mirrored selection, only on the way in, when the reverse prune is
    // already off; on the way out the mirror's replay overwrites the store.
    hostsOwnItem$.pipe(takeUntil(this.alive)).subscribe((hostsOwnItem) => {
      if (hostsOwnItem) {
        this.localHistoryService.updateHistory(DynamicPanelComponent.emptyHistory());
      }
    });

    // One-way pipe: the parent marker selection → the DP-local store, so it renders in the knowledge
    // panel's Selected row and drives its queries.
    //
    // Cut while the DP hosts an item of its own; on the way out the switchMap re-subscribes and replays the record.
    hostsOwnItem$
      .pipe(
        switchMap((hostsOwnItem) =>
          hostsOwnItem
            ? EMPTY
            : this.parentHistoryService.onChangesWithRemovals().pipe(
                // A re-subscription needs the full current selection, not the last removal event.
                map(({ history, removed }, index) => ({ history, removed: index === 0 ? undefined : removed })),
              ),
        ),
        takeUntil(this.alive),
      )
      .subscribe(({ history, removed }) => {
        if (removed) {
          this.localHistoryService.removeFromHistory(removed);
          return;
        }
        this.localHistoryService.updateHistory(history);
      });

    // Reverse pipe — removals only: a marker deselected inside the DP's knowledge panel (the Selected
    // row's ×, Clear or a body click) is removed from the parent too, so the 3rd panel's chip or hub
    // facet un-highlights.
    this.localHistoryService
      .onChanges()
      .pipe(takeUntil(this.alive))
      .subscribe((localHistory) => {
        // Only deselections made in the DP's knowledge panel propagate; while the DP hosts an item of its own,
        // DP-local changes stay local. See SBUX-708 / SV1-194.
        if (this.item || this.dpService.isPanelItemHeld()) {
          return;
        }

        const parentHistory = this.parentHistoryService.getCurrentHistory();
        const prunedParent: StitchFiltersHistoryRecord = {
          knots: parentHistory.knots.filter((knot) => localHistory.knots.some((local) => local.name === knot.name)),
          tags: parentHistory.tags.filter((tag) => localHistory.tags.some((local) => local.name === tag.name)),
          connections: parentHistory.connections.filter((connection) =>
            localHistory.connections.some((local) => local.name === connection.name),
          ),
        };

        const removedSomething =
          prunedParent.knots.length !== parentHistory.knots.length ||
          prunedParent.tags.length !== parentHistory.tags.length ||
          prunedParent.connections.length !== parentHistory.connections.length;

        if (removedSomething) {
          this.parentHistoryService.removeFromHistory({
            knots: parentHistory.knots.filter((marker) => !prunedParent.knots.includes(marker)),
            tags: parentHistory.tags.filter((marker) => !prunedParent.tags.includes(marker)),
            connections: parentHistory.connections.filter((marker) => !prunedParent.connections.includes(marker)),
          });
        }
      });

    // One-way pipe: the main layout's open item → the DP's CONTEXT item.
    // The DP's knowledge panel is about that item: its neighbour lists query it via `contextItems`,
    // so the 4th panel is useful with nothing selected anywhere. It lands in a slot of its own rather than the DP-local
    // `selectedStitchItem`, which means "the item the DP's own form hosts" and is nulled by that
    // form's teardown — the conflation that left the KP contextless after a DP form was closed, and
    // the reason a correctly scoped DP looked dead.
    //
    // The selection is deliberately NOT cleared here. The parent resets its own store on this very
    // event, but only after publishing the item, and that reset reaches the DP-local store through
    // the mirror above. A clear written here would land first, and the reverse prune would read it
    // as the user deselecting everything in the parent.
    this.parentKpService
      .getSelectedStitchItem()
      .pipe(takeUntil(this.alive))
      .subscribe((item) => this.localKpService.setContextStitchItem(item));

    // One-way pipe: global stitchItem context → local.
    // KnotsComponent/TagsComponent/ConnectionsComponent inside the main layout form
    // call setStitchItemKnots/Tags/Connections on the parent KPS. The DP's KP sections
    // read from local KPS, so we need to mirror this data.
    this.parentKpService
      .getStitchItemKnots()
      .pipe(takeUntil(this.alive))
      .subscribe((knots) => this.localKpService.setStitchItemKnots(knots));

    this.parentKpService
      .getStitchItemTags()
      .pipe(takeUntil(this.alive))
      .subscribe((tags) => this.localKpService.setStitchItemTags(tags));

    this.parentKpService
      .getStitchItemConnections()
      .pipe(takeUntil(this.alive))
      .subscribe((connections) => this.localKpService.setStitchItemConnections(connections));

    const unregisterSlidesSpaceSwitch = this.dpService.registerSlidesSpaceSwitch((contextId, isCurrent) => {
      const type = contextId === this.currentUserStore.accountId() ? SpaceType.PERSONAL : SpaceType.SHARED_SPACE;
      return this.spaceContextStore.setContextAfterConfirmation(type, contextId, isCurrent);
    });
    this.destroyRef.onDestroy(unregisterSlidesSpaceSwitch);

    // The service lives above this panel's space context, so the panel reports its space to it: `openInSlides`
    // tags each deck/folder request with it, and a space switch drops one still pending.
    effect(() => {
      const contextId = this.spaceContextStore.contextId();
      untracked(() => this.dpService.setSpaceContext(contextId));
    });

    let confirmInFlight = false;
    effect(() => {
      const next = this.chatTakesOverSource();

      untracked(() => {
        if (this.chatTakesOver() === next || confirmInFlight) {
          return;
        }

        if (next && this.item) {
          confirmInFlight = true;
          this.formChangesModalsService
            .confirmUnsavedChanges(this.item.id)
            .pipe(first(), takeUntil(this.alive))
            .subscribe((canLeave) => {
              confirmInFlight = false;
              if (canLeave) {
                this.chatTakesOver.set(true);
              } else {
                this.chatStore.undock();
              }
            });
          return;
        }

        this.chatTakesOver.set(next);
      });
    });
  }

  /**
   * A fresh record per call: every write is copy-on-write, and the store compares by reference, so a
   * shared constant would make a clear indistinguishable from the previous one.
   */
  private static emptyHistory(): StitchFiltersHistoryRecord {
    return { knots: [], tags: [], connections: [] };
  }

  /**
   * Lifecycle
   */

  ngOnInit(): void {
    // Initialize service with the variant for localStorage key scoping
    this.dpService.init(this.stateKey);

    // Registered here so the guard sees DP-scoped dirty forms (root-scoped service can't).
    // takeUntilDestroyed cuts the prompt if the component dies mid-confirm; the consumer
    // in DynamicPanelService uses take(1) so silent completion is safe.
    this.dpService.setSwitchGuard((current) =>
      this.formChangesModalsService.confirmUnsavedChanges(current.id).pipe(takeUntilDestroyed(this.destroyRef)),
    );

    this.splitViewService.collapsed
      .get(this.splitViewKey)
      .pipe(takeUntil(this.alive))
      .subscribe((minimized: boolean) => {
        const isCollapsing = !this.minimized && minimized;

        // Collapsing the DP while a form is open would destroy the user's unsaved edits:
        // the template `@if (!minimized && !!item)` unmounts `FormsContainerComponent`,
        // which fires `FullFormBaseComponent.ngOnDestroy` → `unregisterForm()`.
        //
        // If we flipped `this.minimized` before the check, the form would be gone by the
        // time `confirmUnsavedChanges()` runs and the lookup would return an empty `of(true)`.
        // Defer the local flag (and therefore the template re-render) until the check resolves.
        //
        // The visual collapse still commits immediately because `SplitViewComponent` subscribes
        // to the same `splitViewService.collapsed` stream and redraws independently — but the
        // form stays mounted and registered, so the dialog can find and prompt for it. On
        // cancel we revert via `splitViewService.open()` and the split view re-expands.
        if (isCollapsing) {
          this.dpService.prepareSurfaceLeave().pipe(switchMap(saved =>
            saved && this.item ? this.formChangesModalsService.confirmUnsavedChanges(this.item.id) : of(saved)))
            .pipe(first(), takeUntil(this.alive))
            .subscribe((canLeave) => {
              if (canLeave) {
                this.minimized = minimized;
                this.changeDetector.detectChanges();
              } else {
                this.splitViewService.open(this.splitViewKey);
              }
            });
          return;
        }

        this.minimized = minimized;
        this.changeDetector.detectChanges();
      });

    this.dpService
      .getApp()
      .pipe(
        takeUntil(this.alive),
        filter((app) => app !== this.selectedApp),
      )
      .subscribe((app) => {
        this.selectedApp = app;
        this.panelApp.set(app);
        this.changeDetector.markForCheck();
      });

    // The label follows the current app's own section as it changes (a list's own default included).
    this.panelApp$
      .pipe(
        switchMap((app) => (app && this.appScopes.get(app)?.selectedTeamId()) || EMPTY),
        takeUntil(this.alive),
      )
      .subscribe(() => this.selectionChanged.update((tick) => tick + 1));

    // The panel STARTS in the workspace and sections the main area shows, once (TW-PANEL-02; the prototype seeds the
    // panel from main at load, app.js :34, and never again). Main's FIRST value counts: a panel saved in another
    // workspace, or a fresh one on a team's address, takes main's at once; an equal value writes nothing. In the
    // layout main publishes before this hook runs; when it publishes later, the lists stay held until its value is
    // applied (`mainWorkspaceApplied`), so no list ever reads the saved workspace first. Later values move nothing:
    // a main switch leaves the panel, its lists and their scroll as they are; only the panel's own menu changes it.
    // Confirmed, not re-asked: the panel's own forms are not in the root service this store consults.
    this.activeSpaceContext
      .contextId$(MAIN_SPACE_CONTEXT)
      .pipe(
        filter((contextId): contextId is string => !!contextId),
        first(),
        takeUntil(this.alive),
      )
      .subscribe((contextId) => {
        if (contextId !== this.spaceContextStore.contextId()) {
          const type = contextId === this.currentUserStore.accountId() ? SpaceType.PERSONAL : SpaceType.SHARED_SPACE;
          this.spaceContextStore.setConfirmedContext(type, contextId);
        }
        this.startInMainSections();
        this.loadWorkspaceIdentity();
        this.allowPanelChat();
        this.mainWorkspaceApplied.set(true);
        this.reopenItemAfterReload();
      });

    this.spaceStateService
      .getCurrentSpace()
      .pipe(takeUntil(this.alive))
      .subscribe((space) => {
        this.space = space;
      });

    // When the item was removed in another panel
    // remove it from DP. A trash signal carries the item's own workspace (measured #2024), so the open item's owner
    // and type are read when the event arrives, whatever workspace the panel or main showed.
    this.stitchEventsService
      .getEvents(() => (this.item?.accountId ? [this.item.accountId] : []), null, [
        SignalStitchEventType.MOVED_TO_TRASH,
        SignalStitchEventType.DELETED,
      ])
      .pipe(
        filter((data) => !!this.item?.id && data.stitchType === this.item.itemType && data.ids.includes(this.item.id)),
        takeUntil(this.alive),
      )
      .subscribe(() => this.dpService.setFormItem(null));
  }

  ngOnDestroy(): void {
    this.dpService.setSwitchGuard(null);
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Actions
   */

  /**
   * Opens the chat from the launcher shown while the DP is collapsed. A docked chat is laid out inside
   * this panel, so it can only be seen with the panel expanded — expand it rather than float the chat,
   * because docked/floating is the user's own preference and not something the panel's state should
   * change. A floating chat is independent of the DP and needs nothing here.
   */
  protected openChat(): void {
    if (this.chatStore.isDocked()) {
      this.splitViewService.open(this.splitViewKey);
      // A full-window session's temporary fold is not the saved collapse: end it as the edge tab and the panel's own reopen do, or the docked chat stays folded.
      this.fullWindow.reopenDp();
    }
    this.chatStore.open();
  }

  /**
   * The panel's own menu: every app's panel section moves to the picked tenant, then the body remounts once. The remount
   * would destroy a quick form in the body, so a pick is refused while one holds unfinished work, in the compact popup's
   * own words; a save in flight finishes where it started. An open item is not under the remount and holds nothing.
   */
  protected pickWorkspace(tenantId: string): void {
    this.closeWorkspaceMenu.next();
    const choice = this.workspaceChoice();
    if (!choice || tenantId === (choice.mine ? choice.identity.memberTenantId : choice.identity.spaceTenantId)) return;
    const held = !this.item || this.selectedApp === null ? this.quickFormDrafts.holding() : null;
    if (held) {
      this.toasterService.show({
        text:
          held === 'saving'
            ? 'Wait until the form finishes saving.'
            : 'Finish or cancel the form before switching the side panel workspace.',
      });
      return;
    }
    for (const scope of this.appScopes.values()) scope?.selectTenant(tenantId);
    this.selectionChanged.update((tick) => tick + 1);
    this.pickCount.update((count) => count + 1);
  }

  /**
   * What the panel lists: its own menu's choice inside a team, else the team it is in. A personal account has one
   * workspace and nothing to switch to, so it is not named (#2494).
   */
  protected workspaceName(): string | undefined {
    const choice = this.workspaceChoice();
    if (choice) return choice.mine ? 'My Workspace' : choice.identity.spaceTitle;
    return this.spaceContextStore.insideTeam() ? this.space?.title : undefined;
  }

  /**
   * Before the body mounts, each app's panel section starts at main's section for the same app in this workspace,
   * read from the slot main's own scope keeps (a 'main'-keyed instance of the same scope class over this store, so
   * the key and the parse are the scope's own; nothing is written on main's side). None stored: the panel's slot is
   * cleared, so no earlier page's pick survives and the panels' team-first default applies.
   */
  private startInMainSections(): void {
    if (this.spaceContextKey !== 'dp') return;
    const shared = this.spaceContextStore.type() === SpaceType.SHARED_SPACE;
    for (const [app, token] of DynamicPanelComponent.WORKSPACE_APPS) {
      const scope = this.appScopes.get(app);
      if (!scope) continue;
      if (app === Application.files) {
        // The panel's Files starts on the TEAM inside a team whatever main's Files shows (prototype app.js :34,
        // `sideScope=app==='files'?'team':scope`); main's member default stays in its own slot. My Workspace: cleared.
        const team = this.spaceContextStore.contextId();
        if (shared && team) scope.selectTenant(team);
        else scope.clearTenant();
        continue;
      }
      const reader = Injector.create({
        parent: this.injector,
        providers: [{ provide: SPACE_CONTEXT_KEY, useValue: MAIN_SPACE_CONTEXT }, token],
      });
      const mainSection = reader.get(token).tenantId();
      reader.destroy();
      if (mainSection === undefined || (mainSection === null && shared)) scope.clearTenant();
      else scope.selectTenant(mainSection);
    }
    this.selectionChanged.update((tick) => tick + 1);
  }

  /**
   * After a reload the panel shows the item it had open (prototype: workspace-panel-state.js). Only the reference was
   * kept, so the item is read again through the panel's own workspace: what the server no longer shows (trashed,
   * access removed) just leaves the panel on its list, without a message.
   */
  private reopenItemAfterReload(): void {
    const restorable = this.dpService.takeRestorableItem(this.spaceContextStore.contextId());
    if (!restorable) return;
    const { reference, intent, requestContext } = restorable;
    const owner = reference.ownerId ? { accountId: reference.ownerId } : {};
    this.stitchServiceFactory
      .getServiceByStitchType(reference.itemType)
      .getItemsBulk([{ id: reference.id, ...owner }], false, requestContext ? { requestContext } : undefined)
      .pipe(
        map((items) => items.find((item) => item.id === reference.id) ?? null),
        catchError(() => of(null)),
        takeUntil(this.alive),
      )
      .subscribe((item) => this.dpService.restoreItem(item, intent, reference));
  }

  private loadWorkspaceIdentity(): void {
    if (this.spaceContextKey !== 'dp' || !this.spaceContextStore.insideTeam()) return;
    this.workspaceIdentity
      .current()
      .pipe(takeUntil(this.alive))
      .subscribe((identity) => this.identity.set(identity));
  }

  /**
   * Team Chat may stand in the panel only while the panel stands on a team and the account has a chat home (#1447 S27,
   * TW-PANEL-02; the app menu offers it on the same terms). Elsewhere a stored Chat opens the first permitted app.
   */
  private allowPanelChat(): void {
    if (this.spaceContextKey !== 'dp' || !this.spaceContextStore.insideTeam()) {
      this.accountChatAvailable.set(false);
      return;
    }
    this.accountService
      .getAccount()
      .pipe(first(), takeUntil(this.alive))
      .subscribe((account) => this.accountChatAvailable.set(chatHomeSpaceId(account) !== null));
  }

  handleAppChange(app: Application): void {
    if (app === this.selectedApp) return;
    this.dpService.prepareSurfaceLeave().pipe(first(), takeUntil(this.alive)).subscribe(saved => {
      if (saved) this.applyAppChange(app);
    });
  }

  private applyAppChange(app: Application): void {
    // No-op if selecting the same app
    if (app === this.selectedApp) {
      return;
    }

    const switchApp = () => {
      this.dpService.setFormItem(null);
      this.advancedSearchService.resetState();
      // The ids-search mode describes the search being reset on the line above, so it dies with it.
      // It has to happen HERE, at the owner: the flag lives on the shell-scoped DynamicPanelService,
      // while the only other resets sit on the panel's folder-menu directive — which is unmounted
      // while a form is open, and on remount sees an already-cleared state with no edge left to
      // react to. The flag then blocks that directive's folder reset forever and strands the panel
      // on an "All Results" its own folder menu no longer offers.
      this.dpService.clearIdsSearchMode();
      this.productAnalytics.track('dynamic_panel_app_changed', { from_app: this.selectedApp ?? null, to_app: app });
      this.dpService.setApp(app);
    };

    // Quick forms live in the list body and would be destroyed by an app switch.
    //
    // No form item open — but the PANEL may still hold something of its own (SBUX-1043: Sheets'
    // fourth-panel workbook, with a live grid and possibly a cell being typed in). Nothing is asked
    // here because `handleAppChange` above already awaited `prepareSurfaceLeave()` (SV1-481), which
    // asks every registered surface guard, Sheets' among them, for both halves of this switch.
    if (!this.item) {
      const held = this.quickFormDrafts.holding();
      if (held) {
        this.toasterService.show({
          text:
            held === 'saving'
              ? 'Wait until the form finishes saving.'
              : 'Finish or cancel the form before switching the side panel app.',
        });
        return;
      }
      switchApp();
      return;
    }

    // Item is open - check for unsaved changes before switching.
    // Pass item.id to scope the check to only this DP form.
    this.formChangesModalsService
      .confirmUnsavedChanges(this.item.id)
      .pipe(first())
      .subscribe((canLeave) => {
        if (canLeave) {
          switchApp();
        }
      });
  }
}
